import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Search, Loader2, ListChecks } from "lucide-react";
import { toast, Toaster } from "sonner";

import "./App.css";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./components/ui/alert-dialog";
import { TaskFormDialog } from "./components/TaskFormDialog";
import { TaskCard } from "./components/TaskCard";
import { StatsBar } from "./components/StatsBar";
import { EmptyState } from "./components/EmptyState";
import { tasksApi } from "./lib/api";
import { cn } from "./lib/utils";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "todo", label: "Todo" },
  { id: "in-progress", label: "In Progress" },
  { id: "done", label: "Done" },
];

function App() {
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = async (status = filter) => {
    try {
      const [list, s] = await Promise.all([
        tasksApi.list(status),
        tasksApi.summary(),
      ]);
      setTasks(list);
      setSummary(s);
    } catch (e) {
      toast.error("Failed to load tasks");
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refresh(filter);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q)
    );
  }, [tasks, search]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (task) => {
    setEditing(task);
    setFormOpen(true);
  };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (editing) {
        await tasksApi.update(editing.id, payload);
        toast.success("Task updated");
      } else {
        await tasksApi.create(payload);
        toast.success("Task created");
      }
      setFormOpen(false);
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(editing ? "Failed to update task" : "Failed to create task");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (task, status) => {
    try {
      await tasksApi.update(task.id, { status });
      toast.success(`Moved to ${status.replace("-", " ")}`);
      await refresh();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await tasksApi.remove(deleteTarget.id);
      toast.success("Task deleted");
      setDeleteTarget(null);
      await refresh();
    } catch (e) {
      toast.error("Failed to delete task");
    }
  };

  return (
    <div data-testid="task-app-container" className="App min-h-screen bg-[#FAF9F6] dark:bg-[#0C0E12]">
      <Toaster richColors position="top-right" />

      {/* Top bar */}
      <header
        data-testid="app-header"
        className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 dark:bg-[#0C0E12]/70 border-b border-slate-200/80 dark:border-slate-800/80"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center">
              <ListChecks className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <p className="font-display text-base font-semibold text-slate-900 dark:text-slate-100">
                Task Studio
              </p>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Executive focus
              </p>
            </div>
          </div>

          <div className="flex-1" />

          <div className="relative hidden sm:block w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="task-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks…"
              className="pl-9 h-10 bg-white/80 dark:bg-slate-900/60"
            />
          </div>

          <Button
            onClick={openCreate}
            data-testid="create-task-btn"
            className="rounded-full h-10 px-5 shadow-sm"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            New task
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-10">
        {/* Hero / page title */}
        <section className="mb-8">
          <motion.h1
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100"
          >
            Make today <span className="text-indigo-600 dark:text-indigo-400">count</span>.
          </motion.h1>
          <p className="mt-2 text-sm md:text-base text-slate-500 dark:text-slate-400 max-w-xl leading-relaxed">
            A quiet place to decide what to do next, do it, and watch it get done.
          </p>
        </section>

        {/* Stats */}
        <section className="mb-8">
          <StatsBar summary={summary} />
        </section>

        {/* Mobile search */}
        <div className="relative sm:hidden mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="task-search-input-mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks…"
            className="pl-9 h-10 bg-white dark:bg-slate-900/60"
          />
        </div>

        {/* Filters */}
        <section
          data-testid="filter-bar"
          className="mb-6 flex items-center gap-2 overflow-x-auto thin-scroll"
        >
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`filter-${f.id}-btn`}
                className={cn(
                  "shrink-0 h-9 px-4 rounded-full text-sm font-medium border",
                  active
                    ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                    : "bg-white dark:bg-[#181E2C] text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600"
                )}
                style={{ transitionProperty: "background-color, border-color, color", transitionDuration: "180ms" }}
              >
                {f.label}
                {summary && (
                  <span className={cn("ml-2 text-xs tabular-nums", active ? "opacity-70" : "text-slate-400")}>
                    {f.id === "all"
                      ? summary.total
                      : f.id === "todo"
                      ? summary.todo
                      : f.id === "in-progress"
                      ? summary.in_progress
                      : summary.done}
                  </span>
                )}
              </button>
            );
          })}
        </section>

        {/* List */}
        <section data-testid="task-list-view">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState onCreate={openCreate} filter={filter} />
          ) : (
            <motion.div layout className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <AnimatePresence mode="popLayout">
                {filtered.map((t) => (
                  <TaskCard
                    key={t.id}
                    task={t}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                    onStatusChange={handleStatusChange}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </section>
      </main>

      {/* Task form */}
      <TaskFormDialog
        open={formOpen}
        onOpenChange={(v) => {
          setFormOpen(v);
          if (!v) setEditing(null);
        }}
        task={editing}
        onSubmit={handleSubmit}
        submitting={submitting}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this task?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.title ? (
                <>
                  “<span className="font-medium text-slate-700 dark:text-slate-300">{deleteTarget.title}</span>” will be
                  removed permanently. This can't be undone.
                </>
              ) : (
                "This action is permanent."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
            >
              Delete task
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default App;
